import { useEffect } from "react";
import { Bell } from "@phosphor-icons/react";
import { toast } from "sonner";
import usePushNotifications from "../hooks/usePushNotifications";

/**
 * Post-chat "Enable notifications?" prompt.
 *
 * Behaviour matches the product spec:
 *   - Shows after a chat ends.
 *   - "Yes" → enable push → never show again.
 *   - "Maybe later" → suppress until at least one MORE chat is ended.
 *
 * State lives in localStorage so the prompt cadence survives reloads and is
 * per-device. Functions are exported so `ChatPage` can:
 *   1. `incrementChatEndedCounter()` once per chat-end (in `endChat`),
 *   2. `shouldShowPostChatPrompt()` to decide whether to mount the modal.
 */

const THRESHOLD_KEY = "akc_push_postchat_threshold";
const DONE_KEY = "akc_push_postchat_done";
const COUNT_KEY = "akc_chats_ended_count";

function readNumber(key) {
  try { return Number(localStorage.getItem(key) || "0"); } catch (e) { return 0; }
}
function writeString(key, value) {
  try { localStorage.setItem(key, value); } catch (e) { /* private mode etc. */ }
}

export function incrementChatEndedCounter() {
  const next = readNumber(COUNT_KEY) + 1;
  writeString(COUNT_KEY, String(next));
  return next;
}

export function shouldShowPostChatPrompt() {
  try {
    if (localStorage.getItem(DONE_KEY) === "1") return false;
  } catch (e) { return false; }
  return readNumber(COUNT_KEY) > readNumber(THRESHOLD_KEY);
}

export default function PostChatNotificationPrompt({ open, onClose }) {
  const push = usePushNotifications();

  // If the user is already subscribed (e.g. enabled via the landing banner
  // earlier), mark "done" and never bother them post-chat.
  useEffect(() => {
    if (open && push.subscribed) {
      writeString(DONE_KEY, "1");
      onClose();
    }
  }, [open, push.subscribed, onClose]);

  if (!open || push.subscribed) return null;

  // iOS Safari (not installed as PWA) can't receive Web Push at all. Show the
  // install copy instead — and let "Maybe later" still backoff so we don't
  // nag them every chat-end.
  const iosNeedsInstall = push.isIos && !push.isStandalone;

  const handleYes = async () => {
    if (iosNeedsInstall) {
      writeString(THRESHOLD_KEY, String(readNumber(COUNT_KEY)));
      toast.message("On iPhone, tap Share → Add to Home Screen first, then open the app from your home screen to enable.");
      onClose();
      return;
    }
    const res = await push.enable();
    if (res.ok) {
      writeString(DONE_KEY, "1");
      toast.success("You're set — we'll send only meaningful alerts.");
      onClose();
    } else if (res.error === "permission_denied") {
      // Browser is now hard-blocked. Mark done so we don't ask forever.
      writeString(DONE_KEY, "1");
      toast.error("Notifications are blocked in your browser. Allow them in settings to receive alerts.");
      onClose();
    } else {
      toast.error("Couldn't enable notifications. Please try again.");
    }
  };

  const handleLater = () => {
    // Pin the threshold at the current chat count — the next chat-end
    // (count > threshold) is when we'll ask again.
    writeString(THRESHOLD_KEY, String(readNumber(COUNT_KEY)));
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4" data-testid="postchat-push-prompt">
      <div className="bg-white rounded-3xl max-w-sm w-full p-6 sm:p-7 shadow-2xl">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 flex items-center justify-center shadow mb-4 mx-auto">
          <Bell size={26} weight="fill" className="text-white" />
        </div>
        <h3 className="font-serif-display text-2xl text-center text-[#3F2D1B] leading-tight">
          Stay tuned to your stars
        </h3>
        <p className="text-sm text-stone-700 text-center mt-3 leading-relaxed">
          {iosNeedsInstall
            ? "Add AstroKuberChat to your Home Screen and we can send auspicious muhurat alerts, planetary changes for your kundali, and updates from your astrologer — straight to your lock screen."
            : "Get auspicious muhurat alerts, planetary changes for your kundali, and replies from astrologers — right on your lock screen. Quiet by design, never spammy."}
        </p>
        <div className="mt-6 space-y-2">
          <button
            onClick={handleYes}
            disabled={push.busy}
            className="w-full px-5 py-2.5 rounded-full bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-white font-semibold text-sm shadow-md hover:opacity-95 disabled:opacity-50"
            data-testid="postchat-push-yes"
          >
            {push.busy ? "Enabling…" : "Yes, enable notifications"}
          </button>
          <button
            onClick={handleLater}
            className="w-full px-5 py-2.5 rounded-full text-stone-700 hover:bg-stone-100 font-medium text-sm"
            data-testid="postchat-push-later"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
